import React, { useEffect } from "react";
import CustomInput from "../components/CustomInput";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  createCoupon,
  getACoupon,
  resetState,
  updateACoupon,
} from "../features/coupon/couponSlice";

// Updated validation schema
let schema = yup.object().shape({
  code: yup.string().required("Coupon Code is Required"),
  expiryDate: yup.date().required("Expiry Date is Required"),
  discountType: yup
    .string()
    .oneOf(["percentage", "fixed"])
    .required("Discount Type is Required"),
  discountValue: yup.number().required("Discount Value is Required"),
  minOrderAmount: yup.number().default(0),
  maxDiscount: yup.number().nullable(),
  usageLimit: yup.number().nullable(),
  perUserLimit: yup.number().default(1),
  isActive: yup.boolean().default(true),
});

const AddCoupon = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const getCouponId = location.pathname.split("/")[3];

  const newCoupon = useSelector((state) => state.coupon);
  const {
    isSuccess,
    isError,
    isLoading,
    createdCoupon,
    couponData,
    updatedCoupon,
  } = newCoupon;

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (getCouponId) {
      dispatch(getACoupon(getCouponId));
    } else {
      dispatch(resetState());
    }
  }, [getCouponId]);

  useEffect(() => {
    if (isSuccess && createdCoupon) {
      toast.success("Coupon Added Successfully!");
    }
    if (isSuccess && updatedCoupon) {
      toast.success("Coupon Updated Successfully!");
      navigate("/admin/coupon-list");
    }
    if (isError) {
      toast.error("Something Went Wrong!");
    }
  }, [isSuccess, isError]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: couponData?.code || "",
      expiryDate: formatDate(couponData?.expiryDate) || "",
      discountType: couponData?.discountType || "percentage",
      discountValue: couponData?.discountValue || "",
      minOrderAmount: couponData?.minOrderAmount || 0,
      maxDiscount: couponData?.maxDiscount || "",
      usageLimit: couponData?.usageLimit || "",
      perUserLimit: couponData?.perUserLimit || 1,
      isActive: couponData?.isActive !== undefined ? couponData.isActive : true,
    },
    validationSchema: schema,
    onSubmit: (values) => {
      if (getCouponId) {
        dispatch(updateACoupon({ id: getCouponId, couponData: values }));
        dispatch(resetState());
      } else {
        dispatch(createCoupon(values));
        formik.resetForm();
        setTimeout(() => {
          dispatch(resetState());
        }, 300);
      }
    },
  });

  return (
    <div>
      <h3 className="mb-4 title">{getCouponId ? "Edit" : "Add"} Coupon</h3>
      <form onSubmit={formik.handleSubmit}>
        <CustomInput
          type="text"
          name="code"
          onChng={formik.handleChange("code")}
          onBlr={formik.handleBlur("code")}
          val={formik.values.code}
          label="Enter Coupon Code"
        />
        <div className="error">{formik.touched.code && formik.errors.code}</div>

        <CustomInput
          type="date"
          name="expiryDate"
          onChng={formik.handleChange("expiryDate")}
          onBlr={formik.handleBlur("expiryDate")}
          val={formik.values.expiryDate}
          label="Expiry Date"
        />
        <div className="error">{formik.touched.expiryDate && formik.errors.expiryDate}</div>

        <div className="mb-3">
          <label>Discount Type</label>
          <select
            className="form-control"
            name="discountType"
            value={formik.values.discountType}
            onChange={formik.handleChange("discountType")}
            onBlur={formik.handleBlur("discountType")}
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
          <div className="error">{formik.touched.discountType && formik.errors.discountType}</div>
        </div>

        <CustomInput
          type="number"
          name="discountValue"
          onChng={formik.handleChange("discountValue")}
          onBlr={formik.handleBlur("discountValue")}
          val={formik.values.discountValue}
          label="Discount Value"
        />
        <div className="error">{formik.touched.discountValue && formik.errors.discountValue}</div>

        <CustomInput
          type="number"
          name="minOrderAmount"
          onChng={formik.handleChange("minOrderAmount")}
          onBlr={formik.handleBlur("minOrderAmount")}
          val={formik.values.minOrderAmount}
          label="Minimum Order Amount"
        />
        <CustomInput
          type="number"
          name="maxDiscount"
          onChng={formik.handleChange("maxDiscount")}
          onBlr={formik.handleBlur("maxDiscount")}
          val={formik.values.maxDiscount}
          label="Maximum Discount (Optional)"
        />
        <CustomInput
          type="number"
          name="usageLimit"
          onChng={formik.handleChange("usageLimit")}
          onBlr={formik.handleBlur("usageLimit")}
          val={formik.values.usageLimit}
          label="Global Usage Limit (Optional)"
        />
        <CustomInput
          type="number"
          name="perUserLimit"
          onChng={formik.handleChange("perUserLimit")}
          onBlr={formik.handleBlur("perUserLimit")}
          val={formik.values.perUserLimit}
          label="Per User Limit"
        />

        <div className="form-check my-3">
          <input
            type="checkbox"
            className="form-check-input"
            name="isActive"
            checked={formik.values.isActive}
            onChange={formik.handleChange("isActive")}
          />
          <label className="form-check-label">Active</label>
        </div>

        <button className="btn btn-success border-0 rounded-3 my-5" type="submit">
          {getCouponId ? "Edit" : "Add"} Coupon
        </button>
      </form>
    </div>
  );
};

export default AddCoupon;
